/* eslint-disable import/first */
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Button, Grid } from 'antd';
import { SendOutlined } from '@ant-design/icons';
import { useErrorHandler } from '../../../../Hooks/useErrorHandler';
import { useTheme } from '../../../../Components/Shared/GlobalContext/ThemeContext';
import { useTranslation } from 'react-i18next';
import { useFileUploadPolicy } from '../../../../Hooks/useFileUploadPolicy';
import { ICON_SIZE, MOBILE_ICON_SIZE, commonIconButtonStyle } from '../../../Shared/Styles/iconButton';
import { shouldUseNativePicker } from '../../../../Utils/nativeFileUpload';
import { useVoiceRecording } from '../../../../Hooks/useVoiceRecording';
import { useFileUpload } from '../../../../Hooks/useFileUpload';
import { useEmojiPicker } from '../../../../Hooks/useEmojiPicker';
import { useMessageBeautify } from '../../../../Hooks/useMessageBeautify';

import FooterInput from './FooterInput/FooterInput';
import FooterUpload from './FooterUpload/FooterUpload';
import FooterVoice from './FooterVoice/FooterVoice';
import FooterPreview from './FooterPreview/FooterPreview';
import FooterEmoji from './FooterEmoji/FooterEmoji';

const MessageFooter = ({ onSendMessage, isSending, focusInputRef, conversationId, initialFileList = [] }) => {
    const [message, setMessage] = useState('');
    const textAreaRef = useRef(null);
    const { handleError } = useErrorHandler();
    const { isDarkMode } = useTheme();
    const { t, i18n } = useTranslation();
    const { policy, accept } = useFileUploadPolicy();
    const isNative = shouldUseNativePicker();
    const isRTL = (typeof i18n.dir === 'function' ? i18n.dir() === 'rtl' : false) || (i18n.language && i18n.language.startsWith('ar'));
    const screens = Grid.useBreakpoint();
    const isMobile = (!!screens.xs && !screens.sm) || false;

    const iconSize = isMobile ? MOBILE_ICON_SIZE : ICON_SIZE;
    // Standard style: padding '4px 8px', minWidth 32.
    // Compact style: padding '4px 4px', minWidth 28.
    const compactButtonStyle = {
        ...commonIconButtonStyle,
        padding: '4px 2px', // Very tight padding
        minWidth: '28px',
        height: '32px'
    };

    // We can use the same compact style for both mobile and desktop if the user wants "reduced space" everywhere.
    // The mobileButtonStyle previously was roughly this.
    const mobileButtonStyle = isMobile ? { ...compactButtonStyle, padding: '4px 4px', height: '28px' } : compactButtonStyle;

    // Use custom hooks for feature logic
    const {
        fileList,
        setFileList,
        uploading,
        uploadingCamera,
        uploadingGallery,
        previewOpen,
        previewImage,
        handleFileUpload,
        handleBulkUpload,
        handleCameraClick,
        handleGalleryClick,
        handlePreview,
        handleChange,
        setPreviewOpen,
        setPreviewImage
    } = useFileUpload({ conversationId, initialFileList, policy, accept, isNative, t, handleError });

    const {
        isRecording,
        recordSeconds,
        pendingVoice,
        waveRef,
        wavePathRef,
        startRecording,
        pauseRecording,
        clearPendingVoice,
        sendPendingVoice,
        formatTime
    } = useVoiceRecording({ onSendMessage, handleFileUpload, handleError, t });

    const {
        emojiOpen,
        setEmojiOpen,
        handleEmojiSelect,
        handleEmojiButtonClick,
        ensureEmojiPreloaded,
        updateSelectionFromEvent
    } = useEmojiPicker({ message, setMessage, isMobile, textAreaRef });

    const {
        beautifying,
        runBeautifyMessage
    } = useMessageBeautify({ conversationId, t, handleError });

    // Reset transient footer state when switching conversations.
    // File attachments are synced from initialFileList inside useFileUpload.
    useEffect(() => {
        setEmojiOpen(false);
        setPreviewOpen(false);
        setPreviewImage('');
    }, [conversationId, setEmojiOpen, setPreviewOpen, setPreviewImage, pendingVoice, clearPendingVoice]);

    const handleSendMessage = useCallback(() => {
        if (message.trim() || fileList.length > 0) {
            // Extract file IDs for attachments
            const attachment_ids = fileList
                .filter(file => file.fileId)
                .map(file => file.fileId);

            onSendMessage({
                message,
                attachment_ids
            });

            setMessage('');
            setFileList([]);
        }
    }, [message, fileList, onSendMessage, setFileList]);

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const hasText = !!(message && message.trim());
    const isSendDisabled = isSending || (!hasText && fileList.length === 0);

    return (
        <div
            onDragOver={(e) => { try { e.preventDefault(); e.stopPropagation(); } catch (err) { } }}
            onDrop={(e) => {
                try {
                    e.preventDefault();
                    e.stopPropagation();
                    const files = Array.from(e?.dataTransfer?.files || []);
                    if (!files.length) return;
                    if (files.length > 1) {
                        handleBulkUpload(files);
                    } else {
                        const file = files[0];
                        (async () => {
                            const uploadedFile = await handleFileUpload(file);
                            if (uploadedFile) {
                                setFileList((prev) => [...prev, uploadedFile]);
                            }
                        })();
                    }
                } catch (err) { }
            }}
            style={{
                padding: '8px 10px',
                borderTop: `1px solid ${isDarkMode ? '#434343' : '#eff3f4'}`,
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: isDarkMode ? '#1f1f1f' : '#fff',
                marginBottom: 0,
                zIndex: 1
            }}
        >
            <FooterPreview
                fileList={fileList}
                setFileList={setFileList}
                handlePreview={handlePreview}
                handleChange={handleChange}
                handleFileUpload={handleFileUpload}
                handleBulkUpload={handleBulkUpload}
                accept={accept}
                uploading={uploading}
                previewOpen={previewOpen}
                previewImage={previewImage}
                setPreviewOpen={setPreviewOpen}
                setPreviewImage={setPreviewImage}
                pendingVoice={pendingVoice}
            />

            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '4px' }}>
                {/* Left side: Attach icons */}
                {!pendingVoice && !isRecording && (
                    <FooterUpload
                        isNative={isNative}
                        accept={accept}
                        onFileUpload={handleFileUpload}
                        onBulkUpload={handleBulkUpload}
                        setFileList={setFileList}
                        uploading={uploading}
                        uploadingCamera={uploadingCamera}
                        uploadingGallery={uploadingGallery}
                        handleCameraClick={handleCameraClick}
                        handleGalleryClick={handleGalleryClick}
                        isMobile={isMobile}
                        mobileButtonStyle={mobileButtonStyle} // Legacy, but kept
                        buttonStyle={compactButtonStyle} // New override
                        ICON_SIZE={ICON_SIZE}
                        iconSize={iconSize}
                    />
                )}

                {/* Center: Text input with integrated beautify bar below */}
                <FooterInput
                    message={message}
                    setMessage={setMessage}
                    handleSendMessage={handleSendMessage}
                    handleKeyDown={handleKeyDown}
                    updateSelectionFromEvent={updateSelectionFromEvent}
                    isRecording={isRecording}
                    pendingVoice={pendingVoice}
                    textAreaRef={textAreaRef}
                    focusInputRef={focusInputRef}
                    isMobile={isMobile}
                    mobileButtonStyle={mobileButtonStyle}
                    buttonStyle={compactButtonStyle}
                    beautifying={beautifying}
                    runBeautifyMessage={runBeautifyMessage}
                    iconSize={iconSize}
                    conversationId={conversationId}
                />

                {/* Right side: Action icons grouped together (Emoji, Voice) */}
                <FooterEmoji
                    isMobile={isMobile}
                    emojiOpen={emojiOpen}
                    setEmojiOpen={setEmojiOpen}
                    handleEmojiButtonClick={handleEmojiButtonClick}
                    ensureEmojiPreloaded={ensureEmojiPreloaded}
                    handleEmojiSelect={handleEmojiSelect}
                    pendingVoice={pendingVoice}
                    isRecording={isRecording}
                    isDarkMode={isDarkMode}
                    mobileButtonStyle={mobileButtonStyle}
                    buttonStyle={compactButtonStyle} // New override
                    ICON_SIZE={ICON_SIZE}
                    iconSize={iconSize}
                    renderPickerOnly={false}
                />

                <FooterVoice
                    isRecording={isRecording}
                    pendingVoice={pendingVoice}
                    recordSeconds={recordSeconds}
                    waveRef={waveRef}
                    wavePathRef={wavePathRef}
                    startRecording={startRecording}
                    pauseRecording={pauseRecording}
                    clearPendingVoice={clearPendingVoice}
                    sendPendingVoice={sendPendingVoice}
                    formatTime={formatTime}
                    setEmojiOpen={setEmojiOpen}
                    hasText={hasText}
                    ICON_SIZE={ICON_SIZE}
                    buttonStyle={compactButtonStyle} // New override
                />

                {/* Send button */}
                {!pendingVoice && !isRecording && (
                    <Button
                        type="primary"
                        icon={<SendOutlined style={{ transform: isRTL ? 'scaleX(-1)' : undefined }} />}
                        onClick={handleSendMessage}
                        loading={isSending}
                        disabled={isSendDisabled}
                        style={{
                            borderRadius: '50%',
                            width: '32px',
                            height: '32px',
                            minWidth: '32px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: isSendDisabled ? (isDarkMode ? '#434343' : '#bfbfbf') : '#1DA1F2',
                            border: 'none',
                            flexShrink: 0,
                            padding: 0,
                        }}
                    />
                )}
            </div>

            {isMobile && (
                <FooterEmoji
                    isMobile={isMobile}
                    emojiOpen={emojiOpen}
                    setEmojiOpen={setEmojiOpen}
                    handleEmojiButtonClick={handleEmojiButtonClick}
                    ensureEmojiPreloaded={ensureEmojiPreloaded}
                    handleEmojiSelect={handleEmojiSelect}
                    pendingVoice={pendingVoice}
                    isRecording={isRecording}
                    isDarkMode={isDarkMode}
                    mobileButtonStyle={mobileButtonStyle}
                    ICON_SIZE={ICON_SIZE}
                    iconSize={iconSize}
                    renderPickerOnly={true}
                />
            )}
        </div>
    );
};
export default MessageFooter;
